import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, Plus, Copy, AlertTriangle } from 'lucide-react';
import {
  integrationsApi,
  type Integration,
  type CreateIntegrationData,
  type IntegrationCredentials,
} from '../../api/endpoints/integrations';
import { queryClient } from '../../providers/QueryProvider';
import { StatCard } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Table, Thead, Th, Td, Tr } from '../../components/ui/Table';
import { Modal } from '../../components/ui/Modal';
import { PageSpinner } from '../../components/ui/Spinner';
import { formatDate } from '../../utils/formatters';

const AVAILABLE_SCOPES = [
  { value: 'read', label: 'Read', description: 'View clients and logs' },
  { value: 'write', label: 'Write', description: 'Register/update clients' },
  { value: 'logs', label: 'Logs', description: 'Create log entries' },
  { value: 'admin', label: 'Admin', description: 'Full access' },
];

const TYPE_OPTIONS = [
  { value: 'tunnel', label: 'Tunnel' },
  { value: 'api', label: 'API' },
  { value: 'webhook', label: 'Webhook' },
  { value: 'monitoring', label: 'Monitoring' },
];

function scopeBadgeVariant(scope: string) {
  switch (scope) {
    case 'admin': return 'red' as const;
    case 'write': return 'yellow' as const;
    case 'logs': return 'purple' as const;
    default: return 'blue' as const;
  }
}

function statusBadgeVariant(status: string) {
  switch (status) {
    case 'active': return 'green' as const;
    case 'pending': return 'yellow' as const;
    case 'revoked': return 'red' as const;
    default: return 'gray' as const;
  }
}

function typeBadgeVariant(type: string) {
  switch (type) {
    case 'tunnel': return 'blue' as const;
    case 'api': return 'purple' as const;
    case 'webhook': return 'green' as const;
    default: return 'gray' as const;
  }
}

function maskApiKey(key: string): string {
  if (!key) return '---';
  if (key.length <= 10) return key;
  return key.substring(0, 6) + '...' + key.substring(key.length - 4);
}

export function IntegrationsPage() {
  const navigate = useNavigate();
  const [showCreate, setShowCreate] = useState(false);
  const [showCredentials, setShowCredentials] = useState(false);
  const [credentials, setCredentials] = useState<IntegrationCredentials | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Integration | null>(null);

  // Create form state
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formType, setFormType] = useState('tunnel');
  const [formScopes, setFormScopes] = useState<string[]>(['read']);
  const [formAllowedIps, setFormAllowedIps] = useState('');
  const [formWebhookUrl, setFormWebhookUrl] = useState('');
  const [formExpiresAt, setFormExpiresAt] = useState('');
  const [copied, setCopied] = useState<string | null>(null);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['integrations'],
    queryFn: () => integrationsApi.list(),
  });

  const { data: statsData } = useQuery({
    queryKey: ['integrations', 'stats'],
    queryFn: () => integrationsApi.stats(),
  });

  const createMutation = useMutation({
    mutationFn: (payload: CreateIntegrationData) => integrationsApi.create(payload),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      setShowCreate(false);
      setCredentials(result.credentials);
      setShowCredentials(true);
      resetForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => integrationsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      setDeleteTarget(null);
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (id: string) => integrationsApi.revoke(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
    },
  });

  function resetForm() {
    setFormName('');
    setFormDescription('');
    setFormType('tunnel');
    setFormScopes(['read']);
    setFormAllowedIps('');
    setFormWebhookUrl('');
    setFormExpiresAt('');
  }

  function toggleScope(scope: string) {
    setFormScopes(prev =>
      prev.includes(scope) ? prev.filter(s => s !== scope) : [...prev, scope],
    );
  }

  function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    const payload: CreateIntegrationData = {
      name: formName,
      description: formDescription || undefined,
      type: formType,
      scopes: formScopes,
      allowed_ips: formAllowedIps ? formAllowedIps.split(',').map(ip => ip.trim()).filter(Boolean) : undefined,
      webhook_url: formWebhookUrl || undefined,
      expires_at: formExpiresAt || undefined,
    };
    createMutation.mutate(payload);
  }

  async function copyToClipboard(text: string, field: string) {
    await navigator.clipboard.writeText(text);
    setCopied(field);
    setTimeout(() => setCopied(null), 2000);
  }

  const integrations = data?.integrations || [];
  const stats = statsData?.stats;

  if (isLoading && !data) return <PageSpinner />;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-lg font-semibold text-gray-800 dark:text-white">Integrations</h1>
          <p className="text-gray-500 dark:text-gray-400 text-xs mt-0.5">Manage API integrations and deployment keys</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => refetch()} className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-1">
            <RefreshCw size={14} />
            Refresh
          </button>
          <button onClick={() => setShowCreate(true)} className="bg-purple-600 text-white px-3 py-1.5 rounded-md hover:bg-purple-700 transition-colors flex items-center gap-1 text-xs">
            <Plus size={14} />
            Create Integration
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <StatCard label="Total" value={integrations.length} color="purple" />
        <StatCard
          label="Active"
          value={stats?.byStatus?.active || integrations.filter(i => i.status === 'active').length}
          color="green"
        />
        <StatCard
          label="Pending"
          value={stats?.byStatus?.pending || integrations.filter(i => i.status === 'pending').length}
          color="yellow"
        />
        <StatCard
          label="Revoked"
          value={stats?.byStatus?.revoked || integrations.filter(i => i.status === 'revoked').length}
          color="red"
        />
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-[#111] rounded-lg shadow-sm border border-gray-200 dark:border-gray-800">
        <Table>
          <Thead>
            <tr>
              <Th>Integration</Th>
              <Th>Deployment ID</Th>
              <Th>Type</Th>
              <Th>Scopes</Th>
              <Th>Status</Th>
              <Th>Clients</Th>
              <Th>Requests</Th>
              <Th>Last Used</Th>
              <Th>Actions</Th>
            </tr>
          </Thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
            {integrations.map(integration => (
              <Tr key={integration._id} onClick={() => navigate(`/integrations/${integration._id}`)}>
                <Td>
                  <div>
                    <span className="font-medium text-purple-600 dark:text-purple-400 hover:underline">
                      {integration.name}
                    </span>
                    {integration.description && (
                      <p className="text-[10px] text-gray-400 truncate max-w-[200px]">{integration.description}</p>
                    )}
                  </div>
                </Td>
                <Td>
                  <code className="text-[10px] bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded font-mono">
                    {integration.deployment_id}
                  </code>
                </Td>
                <Td>
                  <Badge variant={typeBadgeVariant(integration.type)}>{integration.type}</Badge>
                </Td>
                <Td>
                  <div className="flex flex-wrap gap-1">
                    {(integration.scopes || []).map(scope => (
                      <Badge key={scope} variant={scopeBadgeVariant(scope)}>{scope}</Badge>
                    ))}
                  </div>
                </Td>
                <Td>
                  <Badge variant={statusBadgeVariant(integration.status)} dot>{integration.status}</Badge>
                </Td>
                <Td>
                  <span className="text-gray-600 dark:text-gray-400">{integration.client_ids?.length || 0}</span>
                </Td>
                <Td>
                  <span className="text-gray-600 dark:text-gray-400">{integration.usage?.total_requests || 0}</span>
                </Td>
                <Td>
                  <span className="text-gray-500 dark:text-gray-400">
                    {integration.usage?.last_request ? formatDate(integration.usage.last_request) : 'Never'}
                  </span>
                </Td>
                <Td>
                  <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                    {integration.status === 'active' ? (
                      <button
                        onClick={() => { if (confirm('Revoke this integration? It will stop working immediately.')) revokeMutation.mutate(integration._id); }}
                        className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 text-xs font-medium"
                      >
                        Revoke
                      </button>
                    ) : (
                      <button
                        onClick={() => setDeleteTarget(integration)}
                        className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 text-xs font-medium"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </Td>
              </Tr>
            ))}
            {integrations.length === 0 && (
              <tr>
                <td colSpan={9} className="text-center py-8 text-sm text-gray-500 dark:text-gray-400">
                  No integrations yet. Create one to get started.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* Create Integration Modal */}
      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="Create Integration" wide>
        <form onSubmit={handleCreate} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Name *</label>
            <input
              type="text"
              value={formName}
              onChange={e => setFormName(e.target.value)}
              required
              className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-1 focus:ring-purple-500 text-sm dark:bg-gray-700 dark:text-white"
              placeholder="My Integration"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
            <textarea
              value={formDescription}
              onChange={e => setFormDescription(e.target.value)}
              rows={2}
              className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-1 focus:ring-purple-500 text-sm dark:bg-gray-700 dark:text-white"
              placeholder="Optional description"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Type</label>
            <select
              value={formType}
              onChange={e => setFormType(e.target.value)}
              className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-1 focus:ring-purple-500 text-sm dark:bg-gray-700 dark:text-white"
            >
              {TYPE_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Scopes</label>
            <div className="space-y-2">
              {AVAILABLE_SCOPES.map(scope => (
                <label key={scope.value} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formScopes.includes(scope.value)}
                    onChange={() => toggleScope(scope.value)}
                    className="rounded text-purple-600 focus:ring-purple-500 dark:bg-gray-700 dark:border-gray-600"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {scope.label} <span className="text-gray-400 dark:text-gray-500">- {scope.description}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Allowed IPs <span className="text-gray-400 font-normal">(comma-separated, leave empty for all)</span>
            </label>
            <input
              type="text"
              value={formAllowedIps}
              onChange={e => setFormAllowedIps(e.target.value)}
              className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-1 focus:ring-purple-500 text-sm dark:bg-gray-700 dark:text-white"
              placeholder="192.168.1.1, 10.0.0.0/8"
            />
          </div>

          {formType === 'webhook' && (
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Webhook URL</label>
              <input
                type="url"
                value={formWebhookUrl}
                onChange={e => setFormWebhookUrl(e.target.value)}
                className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-1 focus:ring-purple-500 text-sm dark:bg-gray-700 dark:text-white"
                placeholder="https://example.com/webhook"
              />
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Expires At <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <input
              type="datetime-local"
              value={formExpiresAt}
              onChange={e => setFormExpiresAt(e.target.value)}
              className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-1 focus:ring-purple-500 text-sm dark:bg-gray-700 dark:text-white"
            />
          </div>

          {createMutation.isError && (
            <p className="text-xs text-red-600">{(createMutation.error as Error).message}</p>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => setShowCreate(false)}
              className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="px-4 py-1.5 bg-purple-600 text-white rounded-md text-sm hover:bg-purple-700 disabled:opacity-50"
            >
              {createMutation.isPending ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Credentials Modal (one-time display) */}
      <Modal open={showCredentials} onClose={() => setShowCredentials(false)} title="Integration Created!" wide>
        <div className="space-y-4">
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
            <div className="flex items-center gap-2">
              <AlertTriangle size={16} className="text-yellow-600 dark:text-yellow-400" />
              <p className="text-yellow-800 dark:text-yellow-300 text-xs font-medium">
                Save these credentials now! They will not be shown again.
              </p>
            </div>
          </div>

          {credentials && (
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Deployment ID</label>
                <div className="flex">
                  <code className="flex-1 bg-gray-100 dark:bg-gray-700 px-3 py-2 rounded-l-md font-mono text-xs text-gray-800 dark:text-gray-200 overflow-x-auto">
                    {credentials.deployment_id}
                  </code>
                  <button
                    onClick={() => copyToClipboard(credentials.deployment_id, 'deployment_id')}
                    className="bg-gray-200 dark:bg-gray-600 px-3 rounded-r-md hover:bg-gray-300 dark:hover:bg-gray-500 flex items-center"
                  >
                    <Copy size={14} className={copied === 'deployment_id' ? 'text-green-600' : 'text-gray-600 dark:text-gray-300'} />
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Deployment Hash</label>
                <div className="flex">
                  <code className="flex-1 bg-gray-100 dark:bg-gray-700 px-3 py-2 rounded-l-md font-mono text-[10px] text-gray-800 dark:text-gray-200 overflow-x-auto break-all">
                    {credentials.deployment_hash}
                  </code>
                  <button
                    onClick={() => copyToClipboard(credentials.deployment_hash, 'deployment_hash')}
                    className="bg-gray-200 dark:bg-gray-600 px-3 rounded-r-md hover:bg-gray-300 dark:hover:bg-gray-500 flex items-center"
                  >
                    <Copy size={14} className={copied === 'deployment_hash' ? 'text-green-600' : 'text-gray-600 dark:text-gray-300'} />
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">API Key</label>
                <div className="flex">
                  <code className="flex-1 bg-gray-100 dark:bg-gray-700 px-3 py-2 rounded-l-md font-mono text-xs text-gray-800 dark:text-gray-200 overflow-x-auto break-all">
                    {credentials.api_key}
                  </code>
                  <button
                    onClick={() => copyToClipboard(credentials.api_key, 'api_key')}
                    className="bg-gray-200 dark:bg-gray-600 px-3 rounded-r-md hover:bg-gray-300 dark:hover:bg-gray-500 flex items-center"
                  >
                    <Copy size={14} className={copied === 'api_key' ? 'text-green-600' : 'text-gray-600 dark:text-gray-300'} />
                  </button>
                </div>
              </div>

              {credentials.webhook_secret && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Webhook Secret</label>
                  <div className="flex">
                    <code className="flex-1 bg-gray-100 dark:bg-gray-700 px-3 py-2 rounded-l-md font-mono text-xs text-gray-800 dark:text-gray-200 overflow-x-auto break-all">
                      {credentials.webhook_secret}
                    </code>
                    <button
                      onClick={() => copyToClipboard(credentials.webhook_secret!, 'webhook_secret')}
                      className="bg-gray-200 dark:bg-gray-600 px-3 rounded-r-md hover:bg-gray-300 dark:hover:bg-gray-500 flex items-center"
                    >
                      <Copy size={14} className={copied === 'webhook_secret' ? 'text-green-600' : 'text-gray-600 dark:text-gray-300'} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="pt-2">
            <button
              onClick={() => setShowCredentials(false)}
              className="w-full px-4 py-2 bg-purple-600 text-white rounded-md text-sm hover:bg-purple-700"
            >
              Done
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Delete Integration"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Are you sure you want to permanently delete <strong className="text-gray-900 dark:text-white">{deleteTarget?.name}</strong>?
            This action cannot be undone.
          </p>

          {deleteTarget && deleteTarget.client_ids?.length > 0 && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
              <p className="text-yellow-800 dark:text-yellow-300 text-xs">
                This integration has {deleteTarget.client_ids.length} associated client(s). They will be unlinked.
              </p>
            </div>
          )}

          {deleteMutation.isError && (
            <p className="text-xs text-red-600">{(deleteMutation.error as Error).message}</p>
          )}

          <div className="flex justify-end gap-3">
            <button
              onClick={() => setDeleteTarget(null)}
              className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
            >
              Cancel
            </button>
            <button
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget._id)}
              disabled={deleteMutation.isPending}
              className="px-4 py-1.5 bg-red-600 text-white rounded-md text-sm hover:bg-red-700 disabled:opacity-50"
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
